"use client"

import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Plus, Trash2 } from "lucide-react";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import { MultilingualInput, MultilingualTextareaInput, WidgetDirection, WidgetOrientation } from "../GlobalWidgetEditor";
import LinkSelector from "../../LinkSelector";
import { Label } from "@/components/ui/label";
import FileUploadInput from "../../input/MultipleFileUploadInput";

export interface GalleryItem {
    type: 'text' | 'image';
    width?: string;
    direction?: WidgetDirection;
    orientation?: WidgetOrientation;
    title?: string;
    title_en?: string;
    title_ar?: string;
    description?: string;
    description_en?: string;
    description_ar?: string;
    buttonContent?: string;
    buttonContent_en?: string;
    buttonContent_ar?: string;
    link?: string;
    image?: any;
}

interface GalleryWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const GalleryWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: GalleryWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove, update } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    const getError = (path: string) => {
        const errorPath = path.split('.');
        let current = errors;
        for (const key of errorPath) {
            if (current && typeof key === 'string' && key in current) {
                current = (current as any)[key];
            } else {
                return undefined;
            }
        }
        return (current as any)?.message;
    };

    const addItem = () => {
        append({
            type: 'text',
            width: '1/1',
            direction: 'left',
            orientation: 'horizontal',
            title: '',
            title_en: '',
            title_ar: '',
            description: '',
            description_en: '',
            description_ar: '',
            buttonContent: '',
            buttonContent_en: '',
            buttonContent_ar: '',
            link: '',
            image: undefined,
        });
    };

    const removeItem = (galleryIndex: number) => {
        if (fields.length <= 1) return;
        remove(galleryIndex);
    };

    const onTypeChange = (galleryIndex: number, newType: 'text' | 'image') => {
        const currentItem = fields[galleryIndex] as any;
        update(galleryIndex, {
            ...currentItem,
            type: newType,
            ...(newType === 'text' && { image: undefined }), // ✅ اگر متن شد، عکس پاک شود
        });
    };

    return (
        <>
            {fields.map((field, galleryIndex) => {
                const imageError = getError(
                    `${name}.${widgetIndex}.items.${galleryIndex}.image`
                ) as string | undefined;

                const itemType = (field as any).type as 'text' | 'image';

                return (
                    <div key={field.id} className="border p-4 rounded-md space-y-4">
                        <div className="flex justify-between items-center">
                            <Heading level={4}>
                                {dictionary.ui?.form?.galleryItem} {galleryIndex + 1}
                            </Heading>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(galleryIndex)}
                                disabled={fields.length <= 1 || disabled}
                            >
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>

                        {/* ✅ Type + Width + Direction + Orientation (only for images) */}
                        <div className={`grid ${itemType === 'image' ? 'md:grid-cols-4' : 'md:grid-cols-3'} grid-cols-1 gap-3`}>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground">
                                    {dictionary.forms?.type || 'Type'}
                                </label>
                                <Select
                                    value={itemType}
                                    onValueChange={(value) => onTypeChange(galleryIndex, value as 'text' | 'image')}
                                    disabled={disabled}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder="Select item type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="text">{dictionary.forms.text}</SelectItem>
                                        <SelectItem value="image">{dictionary.forms?.image}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground">
                                    {dictionary.forms?.width || 'Width'}
                                </label>
                                <Controller
                                    control={control}
                                    name={`${name}.${widgetIndex}.items.${galleryIndex}.width`}
                                    render={({ field: widthField }) => (
                                        <Select
                                            value={widthField.value || '1/1'}
                                            onValueChange={widthField.onChange}
                                            disabled={disabled}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="1/1">1/1</SelectItem>
                                                <SelectItem value="1/2">1/2</SelectItem>
                                                <SelectItem value="1/3">1/3</SelectItem>
                                                <SelectItem value="1/4">1/4</SelectItem>
                                                <SelectItem value="2/3">2/3</SelectItem>
                                                <SelectItem value="3/4">3/4</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground">
                                    {dictionary.forms?.direction || 'Direction'}
                                </label>
                                <Controller
                                    control={control}
                                    name={`${name}.${widgetIndex}.items.${galleryIndex}.direction`}
                                    render={({ field: directionField }) => (
                                        <Select
                                            value={directionField.value || 'left'}
                                            onValueChange={directionField.onChange}
                                            disabled={disabled}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="left">{dictionary.forms?.left || 'Left'}</SelectItem>
                                                <SelectItem value="right">{dictionary.forms?.right || 'Right'}</SelectItem>
                                                <SelectItem value="center">{dictionary.forms?.center || 'Center'}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>

                            {itemType === 'image' && (
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-muted-foreground">
                                        {dictionary.forms?.orientation + " " + dictionary.forms.image}
                                    </label>
                                    <Controller
                                        control={control}
                                        name={`${name}.${widgetIndex}.items.${galleryIndex}.orientation`}
                                        render={({ field: orientationField }) => (
                                            <Select
                                                value={orientationField.value || 'horizontal'}
                                                onValueChange={orientationField.onChange}
                                                disabled={disabled}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="horizontal">{dictionary.forms?.horizontal || 'Horizontal'}</SelectItem>
                                                    <SelectItem value="vertical">{dictionary.forms?.vertical || 'Vertical'}</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        )}
                                    />
                                </div>
                            )}
                        </div>

                        {/* ✅ Content */}
                        {itemType === 'text' ? (
                            <>
                                <MultilingualInput
                                    baseName={`${name}.${widgetIndex}.items.${galleryIndex}.title`}
                                    label={dictionary.forms?.title}
                                    register={register}
                                    errors={errors}
                                    disabled={disabled}
                                />

                                <MultilingualTextareaInput
                                    baseName={`${name}.${widgetIndex}.items.${galleryIndex}.description`}
                                    label={dictionary.forms?.description}
                                    register={register}
                                    errors={errors}
                                    disabled={disabled}
                                />

                                <MultilingualInput
                                    baseName={`${name}.${widgetIndex}.items.${galleryIndex}.buttonContent`}
                                    label={dictionary.forms?.buttonContent}
                                    register={register}
                                    errors={errors}
                                    disabled={disabled}
                                />

                                <div className="space-y-1">
                                    <Label className="text-xs font-medium text-muted-foreground">
                                        {dictionary.forms?.link}
                                    </Label>
                                    <Controller
                                        control={control}
                                        name={`${name}.${widgetIndex}.items.${galleryIndex}.link`}
                                        render={({ field: linkField }) => (
                                            <LinkSelector
                                                value={linkField.value || ''}
                                                onChange={linkField.onChange}
                                                placeholder={dictionary.forms?.link}
                                            />
                                        )}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <Controller
                                    control={control}
                                    name={`${name}.${widgetIndex}.items.${galleryIndex}.image`}
                                    render={({ field: imageField }) => (
                                        <FileUploadInput
                                            accept="image/*"
                                            disabled={disabled}
                                            onUploadSuccess={(image) => {
                                                if (!Array.isArray(image) && image) imageField.onChange(image);
                                            }}
                                            defaultImages={
                                                imageField.value
                                                    ? [
                                                        {
                                                            ...imageField.value,
                                                            name: imageField.value.name || '',
                                                            description: imageField.value.description || '',
                                                        },
                                                    ]
                                                    : []
                                            }
                                            onDefaultRemove={() => imageField.onChange(undefined)}
                                            error={imageError}
                                        />
                                    )}
                                />

                                <div className="space-y-1">
                                    <Label className="text-xs font-medium text-muted-foreground">
                                        {dictionary.forms?.link}
                                    </Label>
                                    <Controller
                                        control={control}
                                        name={`${name}.${widgetIndex}.items.${galleryIndex}.link`}
                                        render={({ field: linkField }) => (
                                            <LinkSelector
                                                value={linkField.value || ''}
                                                onChange={linkField.onChange}
                                                placeholder={dictionary.forms?.link}
                                            />
                                        )}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                );
            })}

            <Button
                type="button"
                variant="outline"
                onClick={addItem}
                disabled={disabled}
                className="mt-2"
            >
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " " + dictionary.forms.item}

            </Button>
        </>
    );
};